import { Duration, Redacted, Result } from '@blade/std-lib'

import { Error } from './Error'

export { loadScript } from './script-loader/script-loader'

/**
 *
 * @returns true if successful, false otherwise
 */
export const copyToClipboard = async (
  text: string | Redacted.Redacted,
): Promise<boolean> => {
  const textToCopy = Redacted.isRedacted(text) ? Redacted.value(text) : text
  try {
    await navigator.clipboard.writeText(textToCopy)
    document.dispatchEvent(new CustomEvent('app:copy', { detail: { text } }))
    return true
  } catch {
    return copyToClipboardOld(textToCopy)
  }
}

// A hack for old android to get clipboard copy feature ready
function copyToClipboardOld(textToCopy: string): boolean {
  const textarea = document.createElement('textarea')
  textarea.value = textToCopy

  // Move the textarea outside the viewport to make it invisible
  textarea.style.position = 'absolute'
  textarea.style.left = '-99999999px'

  document.body.prepend(textarea)

  // highlight the content of the textarea element
  textarea.select()

  try {
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    textarea.remove()
  }
}

export const cssUrl = (src: string) => `url("${src}")`
export const svgUrl = (id: string) => `url(#${id})`

export const isElementOverflowingHorizontally = ({
  clientWidth,
  scrollWidth,
}: HTMLElement) => {
  return scrollWidth > clientWidth
}

export const isElementOverflowingVertically = ({
  clientHeight,
  scrollHeight,
}: HTMLElement) => {
  return scrollHeight > clientHeight
}

export const isElementOverflowing = (element: HTMLElement) => {
  return (
    isElementOverflowingHorizontally(element) ||
    isElementOverflowingVertically(element)
  )
}

/** Subpixel / bounce tolerance when deciding if a scroll edge still has clipped content. */
const SCROLL_EDGE_TOLERANCE_PX = 1

export type ElementScrollEdges = {
  overflowInlineStart: boolean
  overflowInlineEnd: boolean
  overflowBlockStart: boolean
  overflowBlockEnd: boolean
}

/**
 * Which edges currently clip overflowing content based on scroll position.
 * Unlike {@link isElementOverflowingVertically}, this is edge-aware:
 * at the start of a tall list only the end edge is clipped.
 */
export const getElementScrollEdges = ({
  clientWidth,
  clientHeight,
  scrollWidth,
  scrollHeight,
  scrollLeft,
  scrollTop,
}: HTMLElement): ElementScrollEdges => {
  return {
    overflowInlineStart: scrollLeft > SCROLL_EDGE_TOLERANCE_PX,
    overflowInlineEnd:
      scrollLeft < scrollWidth - clientWidth - SCROLL_EDGE_TOLERANCE_PX,
    overflowBlockStart: scrollTop > SCROLL_EDGE_TOLERANCE_PX,
    overflowBlockEnd:
      scrollTop < scrollHeight - clientHeight - SCROLL_EDGE_TOLERANCE_PX,
  }
}

export const fileToImage = async (file: File) => {
  return new Promise<HTMLImageElement | undefined>((resolve) => {
    if (!file.name) {
      resolve(undefined)
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        resolve(img)
      }
      img.src = e.target?.result as string
    }
    reader.readAsDataURL(file)
  })
}

export const loadImage = async (
  src: string,
  timeout: Duration.Duration = Duration.seconds(30),
) => {
  return new Promise<Result.Result<HTMLImageElement, unknown>>((resolve) => {
    const timeoutId = setTimeout(() => {
      resolve(Result.fail('timeout'))
    }, Duration.toMillis(timeout))
    const img = new Image()
    img.onload = () => {
      clearTimeout(timeoutId)
      resolve(Result.succeed(img))
    }
    img.onerror = (e) => {
      clearTimeout(timeoutId)
      resolve(Result.fail(e))
    }
    img.src = src
  })
}

export const blobToBase64 = (blob: Blob) => {
  return new Promise<Result.Result<string, Error.UnknownError>>(
    (resolve, _) => {
      const reader = new FileReader()
      try {
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(Result.succeed(reader.result))
            return
          }
          resolve(Result.fail(Error.UnknownError.fromUnknown(reader.result)))
        }

        reader.onerror = () =>
          resolve(Result.fail(Error.UnknownError.fromUnknown(reader.error)))

        reader.readAsDataURL(blob)
      } catch (error) {
        resolve(Result.fail(Error.UnknownError.fromUnknown(error)))
      }
    },
  )
}

export const downloadFile = (url: URL | string, fileName: string) => {
  const a = document.createElement('a')
  a.href = url.toString()
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export const isPwa = () => {
  if ('standalone' in navigator && navigator.standalone) {
    return true
  }

  return window.matchMedia('(display-mode: standalone)').matches
}

export const parseJsonUnsafe = <T>(
  raw: string,
): Result.Result<T, Error.UnknownError> => {
  return Result.try({
    try: () => (JSON.parse(raw) ?? undefined) as T,
    catch: (e) => Error.UnknownError.fromUnknown(e),
  })
}

export const getCssVarValue = (name: `--${string}`) => {
  if (typeof window === 'undefined') {
    return ''
  }

  return getComputedStyle(document.documentElement).getPropertyValue(name)
}
